import clsx from 'clsx';

import Checkbox from '@components/checkbox/Checkbox';

import { CustomTableHeaderProps, CustomTableRowData } from './types';

interface CustomTableRowProps<T extends string | number = string> {
  headers: CustomTableHeaderProps[];
  data: CustomTableRowData<T>[];
  handleSelect: (row: CustomTableRowData<T>) => void;
  hasRowClickHandler: boolean;
  selectedRows?: T[];
  hasMultiSelect?: boolean;
  handleCheckboxSelect?: (row: CustomTableRowData<T>) => void;
  disabledRowIds?: T[];
  customClassName?: string;
}

const rowShellClass =
  'flex min-h-12.5 w-full items-center justify-between rounded-lg border border-cofeeNeutral4 px-4 py-2 hover:bg-cofeeHoverBlue text-base';

const CustomTableRow = <T extends string | number = string>({
  headers,
  data,
  handleSelect,
  hasRowClickHandler,
  selectedRows,
  hasMultiSelect,
  handleCheckboxSelect,
  disabledRowIds,
  customClassName = ''
}: CustomTableRowProps<T>) => {
  const getRowClassName = (
    item: CustomTableRowData<T>,
    isInteractive: boolean,
    isDisabled: boolean
  ) =>
    clsx(
      rowShellClass,
      isInteractive && 'cursor-pointer',
      selectedRows?.includes(item.id)
        ? 'bg-cofeeGreyBlue100'
        : 'bg-cofeeNeutral1',
      isDisabled ? 'cursor-not-allowed !bg-cofeeGreyNeutral4' : ''
    );

  return (
    <div
      className={`mt-2 flex min-h-40 w-full flex-col gap-y-1.5 ${customClassName}`}
    >
      {data.map((item) => {
        const isDisabled = Boolean(disabledRowIds?.includes(item.id));
        const isInteractive = hasRowClickHandler && !isDisabled;
        const rowClassName = getRowClassName(item, isInteractive, isDisabled);

        const rowBody = (
          <>
            {hasMultiSelect && (
              <div className="w-5">
                <Checkbox
                  handleChange={() => handleCheckboxSelect?.(item)}
                  isChecked={selectedRows?.includes(item.id)}
                  disabled={disabledRowIds?.includes(item.id)}
                />
              </div>
            )}
            {headers.map((header) => (
              <div
                key={`${item.id}-${header.accessor}`}
                style={{ width: header.width }}
                className={clsx(
                  'px-2 text-base font-medium',
                  disabledRowIds?.includes(item.id)
                    ? 'text-cofeeNeutral6'
                    : 'text-cofeeNeutral10'
                )}
              >
                {item[header.accessor]}
              </div>
            ))}
          </>
        );

        // eslint-disable-next-line no-underscore-dangle
        const errorMessage = item._error_message as string | undefined;

        if (isInteractive) {
          return (
            <div key={item.id}>
              <button
                type="button"
                className={clsx(
                  rowClassName,
                  'text-left text-cofeeNeutral10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cofeePrimary5'
                )}
                onClick={() => handleSelect(item)}
              >
                {rowBody}
              </button>
              {errorMessage && (
                <p className="mt-1 pl-4 text-xs font-medium text-error">
                  {errorMessage}
                </p>
              )}
            </div>
          );
        }

        return (
          <div key={item.id}>
            <div className={rowClassName}>{rowBody}</div>
            {errorMessage && (
              <p className="mt-1 pl-4 text-xs font-medium text-error">
                {errorMessage}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CustomTableRow;
